import { Platform, View } from "react-native";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFocusEffect } from "@react-navigation/native";
import styled, { useTheme } from "styled-components/native";
import Animated, {
  runOnJS,
  useAnimatedScrollHandler,
  withSpring,
} from "react-native-reanimated";
import ScrollButton from "./ScrollButton";
import { getDrawerWidth } from "@navigations/utils/constants";
import Routes from "@navigations/Routes";
import { useColumnWidths } from "../../../hooks/useColumnWidths";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { HorizontalScrollProvider } from "./HorizontalScrollContext";
import { HorizontalScrollMultiplierContext } from "./HorizontalScrollMultiplierContext";
import { calculateEmptyWidth } from "@components/molecules/SearchResultListItem/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useCustomScrollHandler } from "./utils";
import { isEmbeddedFrame } from "@utils/iframeMessaging";
import { useParentResizesIframe } from "../../../hooks/useParentResizesIframe";

const SCROLL_AMOUNT = 220;
const RESIZER_HANDLE_WIDTH = 3;
// Tablet header renders checkbox outside <SpaceBox> (padding + checkbox + padding).
const TABLET_CHECKBOX_COLUMN_WIDTH = SpaceType.Double + 20 + SpaceType.Double;
export const TRAILING_SPACER_WIDTH = 20;

const HorizontalScrollWrapper = ({
  children,
  isListView,
  marginHorizontal,
  xOffset,
  totalItems,
  hiddenColumns = [],
  visibleColumnKeys,
  isLoading,
  isFilterVisible = false,
  priceMode,
  productType,
  hideLeftArrow = false,
  fromSearchResults,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  fromBulkList = false,
  useMobileWebView = false,
  showCheckbox = true,
  showFavorite = true,
}: any) => {
  const theme = useTheme();
  const parentResizesIframe = useParentResizesIframe();
  const usesParentScroll = isEmbeddedFrame() && parentResizesIframe;
  const { deviceWidth: screenWidth } = useIsFoldable();
  const [currentOffset, setCurrentOffset] = useState(0);
  const scrollRef = useRef<Animated.ScrollView>(null);
  const wheelContainerRef = useRef<View>(null);
  const { UpdatedValue } = useCustomScrollHandler(xOffset);
  const { isIFrame } = useUserInfo();
  useFocusEffect(
    useCallback(() => {
      if (
        !fromSearchResults ||
        !(theme.isLargeScreen || theme.isTablet) ||
        xOffset.value <= 0
      ) {
        return;
      }
      let frameId: number | null = requestAnimationFrame(() => {
        frameId = null;
        UpdatedValue.value = xOffset.value;
        scrollRef.current?.scrollTo({ x: xOffset.value, animated: false });
      });
      return () => {
        if (frameId != null) {
          cancelAnimationFrame(frameId);
        }
      };
    }, [fromSearchResults, theme.isLargeScreen, theme.isTablet])
  );

  const columnWidths = useColumnWidths(productType, priceMode, sectionName);
  const filterContainerWidth = getDrawerWidth(
    screenWidth,
    theme,
    Routes.FilterSplitScreen
  );
  const columnWidth = Object.fromEntries(
    Object.entries?.(columnWidths)?.map(([key, value]) => [
      key.toString().toLowerCase(),
      value,
    ])
  );
  const hiddenCol = Object.fromEntries(
    Object.entries?.(hiddenColumns)?.map(([key, value]) => [
      key.toString().toLowerCase(),
      value,
    ])
  );
  // Resolve the actually-rendered set of columns. Prefer the explicit
  // `visibleColumnKeys` prop (kept in sync with the header via
  // `getVisibleColumnKeys`); fall back to filtering by `hiddenColumns`
  // (`is_visible: false`) for legacy callers.
  const visibleColumns = useMemo<Array<[string, number]>>(() => {
    if (Array.isArray(visibleColumnKeys)) {
      return visibleColumnKeys
        .map((key: string) => {
          const lowerKey = key?.toString().toLowerCase();
          const width = Number(columnWidth?.[lowerKey] ?? 0);
          return [lowerKey, width] as [string, number];
        })
        .filter(([, width]) => width > 0);
    }
    return (
      Object.entries?.(columnWidth)?.filter(
        ([key]) => !hiddenCol[key.toString().toLowerCase()]
      ) ?? []
    ).map(([key, width]) => [key, Number(width)] as [string, number]);
  }, [visibleColumnKeys, columnWidth, hiddenCol]);
  // Leading area: SpaceBox scales with multiplier; tablet checkbox column is fixed.
  const leadingWidthParams = useMemo(
    () => ({
      isTablet: theme.isTablet,
      showCheckbox,
      showFavorite,
      useMobileWebView,
    }),
    [theme.isTablet, showCheckbox, showFavorite, useMobileWebView]
  );
  const leadingScalableWidth = useMemo(() => {
    if (!(fromSearchResults || fromBulkList) || isIFrame()) return 0;
    return calculateEmptyWidth(leadingWidthParams);
  }, [fromSearchResults, fromBulkList, leadingWidthParams]);
  const leadingFixedWidth =
    (fromSearchResults || fromBulkList) &&
    theme.isTablet &&
    showCheckbox &&
    !isIFrame()
      ? TABLET_CHECKBOX_COLUMN_WIDTH
      : 0;
  // Resize handles render at a fixed 3px and are NOT scaled by the multiplier.
  // Track them separately so the multiplier math doesn't over-inflate columns.
  const handlesTotalWidth = useMemo(
    () => visibleColumns.length * RESIZER_HANDLE_WIDTH,
    [visibleColumns.length]
  );
  // Sum of widths that DO scale with the multiplier (columns + leading box).
  const scalableBaseWidth = useMemo(() => {
    const columnsSum = visibleColumns.reduce<number>(
      (total, [, width]) => total + width,
      0
    );
    return columnsSum + leadingScalableWidth;
  }, [visibleColumns, leadingScalableWidth]);
  const horizontalMultiplier = useMemo(() => {
    if (!fromSearchResults || !scalableBaseWidth) return 1;
    const totalMargin = marginHorizontal * 2;
    const availableWidth = isFilterVisible
      ? screenWidth - filterContainerWidth - totalMargin
      : screenWidth - totalMargin;
    const scalableAvailable =
      availableWidth -
      handlesTotalWidth -
      leadingFixedWidth -
      TRAILING_SPACER_WIDTH;
    if (scalableAvailable <= scalableBaseWidth) return 1;
    return parseFloat((scalableAvailable / scalableBaseWidth).toFixed(2));
  }, [
    fromSearchResults,
    isFilterVisible,
    screenWidth,
    filterContainerWidth,
    marginHorizontal,
    scalableBaseWidth,
    handlesTotalWidth,
    leadingFixedWidth,
  ]);
  const scaledTotalWidth =
    scalableBaseWidth * horizontalMultiplier +
    handlesTotalWidth +
    leadingFixedWidth +
    TRAILING_SPACER_WIDTH;
  const viewportWidth = isFilterVisible
    ? screenWidth - filterContainerWidth
    : screenWidth;
  const maxOffset = Math.max(
    0,
    scaledTotalWidth - viewportWidth + marginHorizontal * 2
  );
  const isAtRightEnd = currentOffset >= maxOffset - 1;
  // Use original ScrollView for tablet, transform approach for large screen
  const useScrollView = theme.isTablet && !theme.isLargeScreen;
  useEffect(() => {
    if (currentOffset > maxOffset && maxOffset >= 0) {
      const clampedOffset = Math.max(0, maxOffset);
      setCurrentOffset(clampedOffset);
      xOffset.value = clampedOffset;
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ x: clampedOffset, animated: false });
      }
    }
  }, [maxOffset, currentOffset, xOffset]);
  // Sync with xOffset prop on focus
  useFocusEffect(
    useCallback(() => {
      if (
        !fromSearchResults ||
        !(theme.isLargeScreen || theme.isTablet) ||
        xOffset.value <= 0
      ) {
        return;
      }
      setCurrentOffset(xOffset.value);
      // For tablet ScrollView, scroll to the saved position
      if (useScrollView && scrollRef.current) {
        scrollRef.current.scrollTo({ x: xOffset.value, animated: false });
      }
    }, [
      fromSearchResults,
      theme.isLargeScreen,
      theme.isTablet,
      xOffset.value,
      useScrollView,
    ])
  );
  const updateCurrentOffset = useCallback((offset: number) => {
    setCurrentOffset(offset);
  }, []);
  // Scroll handler for tablet's native horizontal ScrollView
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      const offsetX = event.contentOffset.x;
      xOffset.value = offsetX;
      runOnJS(updateCurrentOffset)(offsetX);
    },
  });
  // Update offset for large screen (transform-based approach)
  const updateOffset = useCallback(
    (newOffset: number, animated: boolean = true) => {
      const clampedOffset = Math.max(0, Math.min(newOffset, maxOffset));
      setCurrentOffset(clampedOffset);
      if (animated && Platform.OS === "web") {
        xOffset.value = withSpring(clampedOffset, {
          damping: 20,
          stiffness: 200,
          mass: 0.5,
        });
      } else {
        xOffset.value = clampedOffset;
      }
    },
    [maxOffset, xOffset]
  );
  const handleScroll = useCallback(
    (direction: "left" | "right") => {
      const delta = direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
      if (useScrollView && scrollRef.current) {
        // For tablet, use native scroll
        const newOffset = Math.max(
          0,
          Math.min(currentOffset + delta, maxOffset)
        );
        scrollRef.current.scrollTo({ x: newOffset, animated: true });
        setCurrentOffset(newOffset);
      } else {
        // For large screen, use transform
        updateOffset(currentOffset + delta);
      }
    },
    [currentOffset, updateOffset, useScrollView, maxOffset]
  );
  // Handle mouse wheel horizontal scroll on web (shift+scroll or horizontal scroll)
  // Using useRef to store latest values to avoid stale closures in the native event listener
  const currentOffsetRef = useRef(currentOffset);
  const updateOffsetRef = useRef(updateOffset);
  currentOffsetRef.current = currentOffset;
  updateOffsetRef.current = updateOffset;

  useEffect(() => {
    if (Platform.OS !== "web" || useScrollView) return;

    const element = wheelContainerRef.current as unknown as HTMLElement | null;
    if (!element) return;

    const handleWheel = (event: WheelEvent) => {
      const isHorizontalScroll =
        event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (!isHorizontalScroll) return;
      event.preventDefault();
      const delta = event.shiftKey ? event.deltaY : event.deltaX;
      updateOffsetRef.current(currentOffsetRef.current + delta, false);
    };

    element.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [useScrollView]);

  const showWrapper =
    isListView &&
    (!theme.isMobile || useMobileWebView) &&
    (theme.isLargeScreen || useMobileWebView ? totalItems || isLoading : true);
  const contextValue = useMemo(() => ({ xOffset }), [xOffset]);
  if (!showWrapper) {
    return (
      <HorizontalScrollMultiplierContext.Provider value={1}>
        <HorizontalScrollProvider value={contextValue}>
          {children}
        </HorizontalScrollProvider>
      </HorizontalScrollMultiplierContext.Provider>
    );
  }
  // For tablet: use native horizontal ScrollView (original behavior)
  if (useScrollView) {
    return (
      <HorizontalScrollProvider value={contextValue}>
        <View style={{ position: "relative" }}>
          {currentOffset > 0 && totalItems && !hideLeftArrow ? (
            <ScrollButton
              side="left"
              onPress={() => handleScroll("left")}
              iconColor={theme["core-06"]}
              useMobileWebView={useMobileWebView}
            />
          ) : null}
          <Container
            ref={scrollRef}
            bounces={false}
            horizontal={true}
            {...(theme.isLargeScreen && {
              width: isFilterVisible
                ? screenWidth - filterContainerWidth
                : screenWidth,
            })}
            {...(fromSearchResults && {
              contentContainerStyle: {
                width:
                  !isFilterVisible && screenWidth > scaledTotalWidth
                    ? "100%"
                    : scaledTotalWidth,
              },
            })}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            style={{
              borderRadius: SpaceType.Basic,
              marginHorizontal: marginHorizontal,
              ...(theme.isTablet || theme.isLargeScreen
                ? { paddingTop: 16 }
                : {}),
              ...((theme.isLargeScreen || theme.isTablet) &&
              !isFilterVisible &&
              fromSearchResults &&
              screenWidth > scaledTotalWidth
                ? {
                    justifyContent: "center",
                  }
                : {}),
            }}
          >
            {children}
          </Container>
          {currentOffset >= 0 &&
          !isAtRightEnd &&
          totalItems &&
          scaledTotalWidth > viewportWidth ? (
            <ScrollButton
              side="right"
              onPress={() => handleScroll("right")}
              iconColor={theme["core-06"]}
              useMobileWebView={useMobileWebView}
            />
          ) : null}
        </View>
      </HorizontalScrollProvider>
    );
  }
  // For large screen: use transform-based approach (vertical scrollbar always visible)
  return (
    <HorizontalScrollMultiplierContext.Provider value={horizontalMultiplier}>
      <View
        style={{
          ...(Platform.OS !== "web"
            ? { position: "relative" }
            : usesParentScroll
            ? { width: "100%", maxWidth: "100%" }
            : { flex: 1 }),
        }}
      >
        {UpdatedValue.value > 0 && totalItems && !hideLeftArrow ? (
          <ScrollButton
            side="left"
            onPress={() => handleScroll("left")}
            iconColor={theme["core-06"]}
            useMobileWebView={useMobileWebView}
          />
        ) : null}
        <Container
          ref={scrollRef}
          bounces={false}
          horizontal={true}
          nestedScrollEnabled={true}
          {...((Platform.OS === "web" || theme.isLargeScreen) && {
            width: isFilterVisible
              ? screenWidth - filterContainerWidth
              : screenWidth,
          })}
          {...(fromSearchResults && {
            contentContainerStyle: {
              width: scaledTotalWidth,
            },
          })}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          style={{
            borderRadius: SpaceType.Basic,
            marginHorizontal: marginHorizontal,
            ...(theme.isTablet || theme.isLargeScreen
              ? { paddingTop: 16 }
              : {}),
            ...(usesParentScroll ? { flexGrow: 0, flexShrink: 0 } : {}),
          }}
        >
          {children}
        </Container>
        {UpdatedValue.value >= 0 &&
        !isAtRightEnd &&
        totalItems &&
        scaledTotalWidth >
          (isFilterVisible
            ? screenWidth - filterContainerWidth
            : screenWidth) ? (
          <ScrollButton
            side="right"
            onPress={() => handleScroll("right")}
            iconColor={theme["core-06"]}
            useMobileWebView={useMobileWebView}
          />
        ) : null}
      </View>
    </HorizontalScrollMultiplierContext.Provider>
  );
};

const Container = styled(Animated.ScrollView)`
  padding: 0;
`;
const areVisibleKeysEqual = (a: any, b: any) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};
export default memo(HorizontalScrollWrapper, (prevProps, nextProps) => {
  const selectedItemsEqual =
    (!prevProps.selectedItems && !nextProps.selectedItems) ||
    (Array.isArray(prevProps.selectedItems) &&
      Array.isArray(nextProps.selectedItems) &&
      prevProps?.selectedItems?.length === nextProps?.selectedItems?.length &&
      prevProps?.selectedItems?.every(
        (item, index) => item === nextProps.selectedItems[index]
      ));
  return (
    selectedItemsEqual &&
    prevProps.isListView === nextProps.isListView &&
    prevProps.useMobileWebView === nextProps.useMobileWebView &&
    prevProps.marginHorizontal === nextProps.marginHorizontal &&
    prevProps.totalItems === nextProps.totalItems &&
    prevProps.hiddenColumns === nextProps.hiddenColumns &&
    areVisibleKeysEqual(
      prevProps.visibleColumnKeys,
      nextProps.visibleColumnKeys
    ) &&
    prevProps.showCheckbox === nextProps.showCheckbox &&
    prevProps.showFavorite === nextProps.showFavorite &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isFilterVisible === nextProps.isFilterVisible &&
    prevProps.priceMode === nextProps.priceMode &&
    prevProps.productType === nextProps.productType &&
    prevProps.sectionName === nextProps.sectionName &&
    prevProps.hasAnyOfferDetails === nextProps.hasAnyOfferDetails &&
    prevProps.fromSearchResults === nextProps.fromSearchResults
  );
});
